import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { InvoiceService } from '../../../services/invoice.service';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from '../../../services/api.services';
import { MerchantService } from '../../../services/merchant.service';
import { SpinnerComponent } from '../../ui/spinner/spinner.component';
import { PayerHeaderComponent } from '../../layout/payer-header/payer-header.component';

@Component({
    selector: 'app-get-invoice',
    templateUrl: './get-invoice.component.html',
    imports: [FormsModule, CommonModule, SpinnerComponent, PayerHeaderComponent],
    styleUrl: './get-invoice.component.scss'
})
export class GetInvoiceComponent implements OnInit {
  
  invoiceNumber: string = '';
  isLoadingSpinner: boolean = false;
  isLoading: boolean = false;
  isLoading2: boolean = false;
  errorMessage: string | null = null;
  invoiceData: any | null = null;
  // Presentation-only breakdown derived from invoiceData.Line/TxnTaxDetail —
  // see buildInvoiceBreakdown(). QuickBooks stays the source of truth for
  // TotalAmt/Balance; this only interprets the line-item structure QuickBooks
  // already returns so the template doesn't have to parse it inline.
  invoiceBreakdown: any = null;
  isPaid: boolean = false;
  companyInfo: any = null;
  companyLogo: string = '';
  isRouteWithInvoice: boolean = false;

  paymentData = {
    TotalAmt: 0,
    CustomerRef: {
      value: '',
    },
    Line: [
      {
        Amount: 1.0,
        LinkedTxn: [],
      },
    ],
  };
  paymentLink: string | null = null;

  constructor(
    private _invoiceService: InvoiceService,
    private _merchantService: MerchantService,
    private router: Router,
    private route: ActivatedRoute,
    private apiService: ApiService
  ) {}

  ngOnInit(): void {
    this.isLoadingSpinner = true;
    this.paymentLink = this.route.snapshot.paramMap.get('paymentLink');
    this.invoiceNumber = this.route.snapshot.paramMap.get('invoiceNumber') || '';
    this.isRouteWithInvoice = !!this.invoiceNumber;
    this._merchantService.getMerchantByLink(this.paymentLink).subscribe({
      next: (response) => {
        this._merchantService.setCurrentMerchant(response);
        // Merchant branding (logo/initials) must be available for the payer
        // header as soon as the merchant is known — not only after a
        // successful invoice fetch — so the payer always knows which
        // business they're paying, even on the lookup form itself.
        this.companyLogo = response?.strictly?.logoUrl || '';
        this._invoiceService
          .getCompanyInfo(response.id, response.intuit.companyId)
          .subscribe({
            next: (companyInfo) => {
              if (companyInfo.QueryResponse.CompanyInfo) {
                this.companyInfo = companyInfo.QueryResponse.CompanyInfo[0];
              }
              if(this.invoiceNumber) {
                this.getInvoice();
              } else {
                this.isLoadingSpinner = false;
              }
            },
            error: () => {
              this.router.navigate(['/error'], {
                queryParams: {
                  code: '002',
                  message:
                    'Oops, something went wrong. Please try again later.',
                },
              });
            },
          });
      },
      error: () => {
        this.router.navigate(['/error'], {
          queryParams: {
            code: '003',
            message: 'Oops, something went wrong. Please try again later.',
          },
        });
      },
    });
  }

  validateNumber(event: KeyboardEvent) {
    const charCode = event.charCode;
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
    }
  }

  getInvoice(): void {
    if (!this.invoiceNumber) {
      this.errorMessage = 'Please enter a valid invoice number.';
      return;
    }

    this.isLoading = true;
    this.errorMessage = null;
    this.isPaid = false;
    const currentMerchant = this._merchantService.getCurrentMerchant();
    this.companyLogo = currentMerchant.strictly.logoUrl;

    this._invoiceService
      .getInvoiceByDocNumber(
        this.invoiceNumber,
        currentMerchant.id,
        currentMerchant.intuit.companyId
      )
      .subscribe(
        (data: any) => {
          this.isLoading = false;
          if (data.QueryResponse.Invoice) {
            this.invoiceNumber = '';
            const invoice = data.QueryResponse.Invoice[0];
            this._invoiceService.setInvoice(invoice);
            // The invoice response already tells us whether it's paid
            // (Balance <= 0) — show the same invoice data either way and let
            // the template render the "Paid" state instead of routing a
            // fully-paid invoice through the error-message branch back to
            // the lookup form. No QuickBooks logic or calculation changed.
            this.invoiceData = invoice;
            this.invoiceBreakdown = this.buildInvoiceBreakdown(invoice);
            this.isPaid = invoice.Balance <= 0;
          } else {
            this.errorMessage = 'Invoice not found.';
            this.invoiceData = null;
            this.invoiceNumber = '';
          }
          this.isLoadingSpinner = false;
        },
        (error: any) => {
          this.isLoading = false;
          this.errorMessage = 'An error occurred while fetching the invoice.';
          this.isLoadingSpinner = false;
          console.error(error);
        }
      );
  }

  goToPaymentLink(): void {
    this.router.navigate(['/payment-link']);
  }

  searchAnotherInvoice(): void {
    this.invoiceData = null;
    this.invoiceBreakdown = null;
    this.isPaid = false;
  }

  // Interprets the QuickBooks Invoice.Line/TxnTaxDetail structure into the
  // flat set of values the template renders — does not touch or recompute
  // TotalAmt/Balance, and never mutates `invoice`. See PAIERLY_INVOICE_...
  // task notes: QuickBooks special lines (SubTotalLineDetail,
  // DiscountLineDetail, the shipping pseudo-item) don't always carry an
  // `Id`, so they can't be told apart from real sales lines by that alone.
  private buildInvoiceBreakdown(invoice: any): any {
    const lines: any[] = Array.isArray(invoice?.Line) ? invoice.Line : [];

    const isShippingLine = (line: any): boolean => {
      if (line?.DetailType !== 'SalesItemLineDetail') {
        return false;
      }
      const itemRef = line.SalesItemLineDetail?.ItemRef;
      // QuickBooks' own shipping pseudo-item usually has value
      // "SHIPPING_ITEM_ID", but some companies have a real Shipping item
      // configured with an ordinary numeric value and a "Shipping" name —
      // check both rather than assuming the literal placeholder value.
      return (
        itemRef?.value === 'SHIPPING_ITEM_ID' ||
        (itemRef?.name || '').trim().toLowerCase() === 'shipping'
      );
    };

    const shippingLine = lines.find(isShippingLine) || null;

    const salesLines = lines.filter(
      (line) => line?.DetailType === 'SalesItemLineDetail' && !isShippingLine(line)
    );

    const subtotalLine =
      lines.find((line) => line?.DetailType === 'SubTotalLineDetail') || null;

    const discountLine =
      lines.find((line) => line?.DetailType === 'DiscountLineDetail') || null;
    const discountPercent =
      discountLine?.DiscountLineDetail?.PercentBased &&
      typeof discountLine.DiscountLineDetail?.DiscountPercent === 'number'
        ? discountLine.DiscountLineDetail.DiscountPercent
        : null;

    // A single, unambiguous rate can be labeled ("Tax (8%)"); more than one
    // TaxLine means no single percentage is representative, so only the
    // amount (still sourced from TxnTaxDetail.TotalTax, not summed here) is
    // shown.
    const taxLines = invoice?.TxnTaxDetail?.TaxLine;
    const singleTaxLine = Array.isArray(taxLines) && taxLines.length === 1 ? taxLines[0] : null;
    const taxPercent =
      singleTaxLine?.TaxLineDetail?.PercentBased &&
      typeof singleTaxLine.TaxLineDetail?.TaxPercent === 'number'
        ? singleTaxLine.TaxLineDetail.TaxPercent
        : null;
    const taxAmount =
      typeof invoice?.TxnTaxDetail?.TotalTax === 'number' ? invoice.TxnTaxDetail.TotalTax : null;

    return {
      salesLines,
      subtotal: typeof subtotalLine?.Amount === 'number' ? subtotalLine.Amount : null,
      discountAmount: typeof discountLine?.Amount === 'number' ? discountLine.Amount : null,
      discountPercent,
      shippingAmount: typeof shippingLine?.Amount === 'number' ? shippingLine.Amount : null,
      taxAmount,
      taxPercent,
      amountPaid: (invoice?.TotalAmt ?? 0) - (invoice?.Balance ?? 0),
    };
  }
}
